<template>
    <!-- estado de tela de detalhe quando o registro da rota nao existe (404) ou nao carregou -->
    <section class="muven-card muven-resource-missing"
        :class="{ 'is-failed': isFailed }"
        :role="isFailed ? 'alert' : 'status'"
        aria-labelledby="muven-resource-missing-title">

        <div class="muven-resource-missing-art" aria-hidden="true">
            <svg viewBox="0 0 160 140" width="160" height="140" focusable="false">
                <!-- ficha do registro -->
                <g class="art-sheet">
                    <rect x="22" y="14" width="92" height="112" rx="8" class="art-paper" />
                    <path d="M92 14 L114 36 L92 36 Z" class="art-fold" />
                    <rect x="36" y="44" width="44" height="7" rx="3.5" class="art-line art-line-strong" />
                    <rect x="36" y="60" width="62" height="5" rx="2.5" class="art-line" />
                    <rect x="36" y="72" width="54" height="5" rx="2.5" class="art-line" />
                    <rect x="36" y="84" width="58" height="5" rx="2.5" class="art-line" />
                    <rect x="36" y="96" width="34" height="5" rx="2.5" class="art-line" />
                </g>

                <!-- 404: lupa que procura e nao acha -->
                <g v-if="!isFailed" class="art-lens">
                    <circle cx="112" cy="86" r="22" class="art-lens-glass" />
                    <path d="M128 102 L146 120" class="art-lens-handle" />
                    <path d="M106 80 L118 92 M118 80 L106 92" class="art-lens-mark" />
                </g>

                <!-- falha: conexao interrompida -->
                <g v-else class="art-plug">
                    <circle cx="112" cy="86" r="22" class="art-plug-badge" />
                    <path d="M101 86 h6 M117 86 h6" class="art-plug-wire" />
                    <path d="M110 76 L106 86 L114 86 L110 96" class="art-plug-bolt" />
                </g>
            </svg>
        </div>

        <div class="muven-resource-missing-body">
            <p class="muven-resource-missing-eyebrow">
                {{ isFailed ? 'Falha ao carregar' : 'Erro 404' }}
            </p>

            <h2 id="muven-resource-missing-title"
                ref="title"
                class="muven-resource-missing-title"
                tabindex="-1">
                <template v-if="!isFailed">Não encontramos {{article}} {{resource}}</template>
                <template v-else>Não conseguimos carregar {{article}} {{resource}}</template>
                <code v-if="!!identifier" class="muven-resource-missing-id">#{{identifier}}</code>
            </h2>

            <template v-if="!isFailed">
                <p class="muven-resource-missing-lead">
                    {{articleUpper}} {{resource}} que você procura não existe ou não está disponível para esta empresa.
                    Isso costuma acontecer quando:
                </p>

                <ul class="muven-resource-missing-causes">
                    <li>o endereço foi digitado errado ou veio incompleto;</li>
                    <li>{{article}} {{resource}} foi {{removedLabel}};</li>
                    <li v-if="!!subscriber.name">
                        pertence a outra empresa. Você está em <b>{{subscriber.name}}</b>,
                        troque no menu do topo se for o caso.
                    </li>
                    <li v-else>pertence a outra empresa que você também acessa.</li>
                </ul>
            </template>

            <p v-else class="muven-resource-missing-lead">
                Pode ser uma instabilidade momentânea na conexão ou no servidor.
                Tente novamente em alguns segundos. Se o problema continuar, fale com o suporte
                e informe o código {{article}} {{resource}}.
            </p>

            <div class="muven-resource-missing-actions">
                <button v-if="isFailed"
                    type="button"
                    class="btn btn-primary"
                    @click="$emit('retry')">
                    <fa icon="redo-alt" class="mr-2" />
                    Tentar novamente
                </button>

                <!-- sempre volta para a lista do recurso, nunca para o historico do navegador -->
                <router-link :to="listPath"
                    class="btn"
                    :class="isFailed ? 'btn-outline-secondary' : 'btn-primary'">
                    <fa icon="arrow-left" class="mr-2" />
                    {{listLabel}}
                </router-link>
            </div>

            <details v-if="isFailed && !!errorMessage" class="muven-resource-missing-details">
                <summary>Detalhes técnicos</summary>
                <code>{{errorMessage}}</code>
            </details>
        </div>
    </section>
</template>

<script>
import { mapState } from 'vuex'

const APP_TITLE = 'muven'

export default {
    name: 'ResourceNotFoundCmp',
    props: {
        // nome do registro em minusculo: 'pedido', 'produto', 'canal'
        resource: {
            type: String,
            required: true,
        },
        // concordancia: 'o' (o pedido) ou 'a' (a marca)
        gender: {
            type: String,
            default: 'o',
        },
        identifier: {
            type: [String, Number],
            default: '',
        },
        listPath: {
            type: String,
            required: true,
        },
        listLabel: {
            type: String,
            required: true,
        },
        // 'not-found' | 'failed' (ver ResourceErrorHelper)
        kind: {
            type: String,
            default: 'not-found',
        },
        errorMessage: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            previousTitle: document.title,
        }
    },
    mounted() {
        this._updateDocumentTitle()
        // leva o leitor de tela direto para a explicacao
        this.$nextTick(() => this.$refs.title && this.$refs.title.focus({ preventScroll: true }))
    },
    beforeDestroy() {
        document.title = this.previousTitle || APP_TITLE
    },
    watch: {
        kind() {
            this._updateDocumentTitle()
        },
    },
    methods: {
        _updateDocumentTitle() {
            const resource = this.resource.charAt(0).toUpperCase() + this.resource.slice(1)
            const status = this.isFailed ? 'não carregou' : `não encontrad${this.gender}`

            document.title = `${resource} ${status} - ${APP_TITLE}`
        },
    },
    computed: {
        ...mapState({
            subscriber: state => state.subscriber,
        }),
        isFailed() {
            return this.kind === 'failed'
        },
        article() {
            return this.gender
        },
        articleUpper() {
            return this.gender.toUpperCase()
        },
        removedLabel() {
            return `removid${this.gender}`
        },
    },
}
</script>

<style lang="scss" scoped>
$ease-out: cubic-bezier(.22, 1, .36, 1);

.muven-resource-missing {
    display: flex;
    align-items: center;
    gap: 40px;
    max-width: 820px;
    margin: 24px auto;
    padding: 40px 48px;
    border-color: var(--border-subtle);
    border-radius: 6px;
    overflow: hidden;

    // faixa de marca no topo, a mesma "assinatura" do sublinhado do titulo da tela
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: var(--brand-gradient);
    }

    &.is-failed::before {
        background: var(--warning-solid);
    }
}

/* ── Ilustracao ── */

.muven-resource-missing-art {
    flex: 0 0 auto;
    display: grid;
    place-items: center;
    width: 200px;
    height: 200px;
    border-radius: 50%;
    background: radial-gradient(circle at 50% 45%, var(--tint-brand-bg) 0, var(--tint-brand-soft) 62%, transparent 63%);
    animation: missing-pop .6s $ease-out both;

    .is-failed & {
        background: radial-gradient(circle at 50% 45%, var(--warning-bg) 0, var(--surface-2) 62%, transparent 63%);
    }

    svg {
        overflow: visible;
    }
}

.art-paper {
    fill: var(--surface-1);
    stroke: var(--tint-brand-border);
    stroke-width: 2;
}

.art-fold {
    fill: var(--tint-brand-hover);
    stroke: var(--tint-brand-border);
    stroke-width: 2;
    stroke-linejoin: round;
}

.art-line {
    fill: var(--surface-3);
}

.art-line-strong {
    fill: var(--tint-brand-border);
}

.art-lens {
    transform-origin: 112px 86px;
    animation: missing-search 1.6s .35s $ease-out both;
}

.art-lens-glass {
    fill: color-mix(in srgb, var(--surface-1) 85%, transparent);
    stroke: var(--brand-soft);
    stroke-width: 5;
}

.art-lens-handle {
    stroke: var(--brand-soft);
    stroke-width: 8;
    stroke-linecap: round;
}

.art-lens-mark {
    stroke: var(--brand);
    stroke-width: 3.5;
    stroke-linecap: round;
}

.art-plug-badge {
    fill: var(--surface-1);
    stroke: var(--warning-solid);
    stroke-width: 4;
}

.art-plug-wire {
    stroke: var(--warning-fg);
    stroke-width: 3.5;
    stroke-linecap: round;
}

.art-plug-bolt {
    fill: none;
    stroke: var(--warning-fg);
    stroke-width: 3.5;
    stroke-linecap: round;
    stroke-linejoin: round;
}

/* ── Texto ── */

.muven-resource-missing-body {
    flex: 1 1 auto;
    min-width: 0;

    > * {
        animation: missing-rise .5s $ease-out both;
    }

    > :nth-child(2) { animation-delay: .06s; }
    > :nth-child(3) { animation-delay: .12s; }
    > :nth-child(4) { animation-delay: .18s; }
    > :nth-child(n+5) { animation-delay: .24s; }
}

.muven-resource-missing-eyebrow {
    margin: 0 0 8px;
    font-size: .75rem;
    font-weight: 700;
    letter-spacing: .12em;
    text-transform: uppercase;
    color: var(--brand-soft);

    .is-failed & {
        color: var(--warning-fg);
    }
}

.muven-resource-missing-title {
    margin: 0 0 12px;
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.3;
    color: var(--text-strong);
    outline: none;
}

.muven-resource-missing-id {
    display: inline-block;
    margin-left: 4px;
    padding: 1px 8px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: .8em;
    font-weight: 600;
    color: var(--brand-strong);
    background: var(--tint-brand-bg);
    border: 1px solid var(--tint-brand-border);
    border-radius: 4px;
    word-break: break-all;
    vertical-align: .08em;
}

.muven-resource-missing-lead {
    margin: 0 0 8px;
    color: var(--text-soft);
    line-height: 1.55;
}

.muven-resource-missing-causes {
    margin: 0 0 24px;
    padding-left: 0;
    list-style: none;
    color: var(--text-soft);

    li {
        position: relative;
        padding: 3px 0 3px 20px;
        line-height: 1.5;

        &::before {
            content: '';
            position: absolute;
            left: 4px;
            top: .85em;
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: var(--brand-soft);
        }
    }

    b {
        color: var(--text-strong);
    }
}

.is-failed .muven-resource-missing-lead {
    margin-bottom: 24px;
}

.muven-resource-missing-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;

    .btn {
        display: inline-flex;
        align-items: center;
    }
}

.muven-resource-missing-details {
    margin-top: 20px;
    font-size: .85rem;
    color: var(--text-muted);

    summary {
        cursor: pointer;
        width: max-content;
    }

    code {
        display: block;
        margin-top: 8px;
        padding: 10px 12px;
        color: var(--text-soft);
        background: var(--surface-2);
        border: 1px solid var(--border-subtle);
        border-radius: 4px;
        white-space: pre-wrap;
        word-break: break-word;
    }
}

/* ── Movimento ── */

@keyframes missing-pop {
    from { opacity: 0; transform: scale(.92); }
    to   { opacity: 1; transform: scale(1); }
}

@keyframes missing-rise {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
}

// a lupa varre a ficha e para no centro, sem loop
@keyframes missing-search {
    0%   { opacity: 0; transform: translate(-46px, -34px) rotate(-12deg); }
    30%  { opacity: 1; }
    55%  { transform: translate(-22px, 6px) rotate(6deg); }
    80%  { transform: translate(4px, -6px) rotate(-3deg); }
    100% { opacity: 1; transform: translate(0, 0) rotate(0); }
}

@media (prefers-reduced-motion: reduce) {
    .muven-resource-missing-art,
    .muven-resource-missing-body > *,
    .art-lens {
        animation: none;
    }
}

/* ── Mobile ── */

@media (max-width: 767px) {
    .muven-resource-missing {
        flex-direction: column;
        align-items: stretch;
        gap: 16px;
        margin: 8px 0;
        padding: 28px 20px;
        text-align: left;
    }

    .muven-resource-missing-art {
        width: 150px;
        height: 150px;
        align-self: center;

        svg {
            width: 120px;
            height: 105px;
        }
    }

    .muven-resource-missing-title {
        font-size: 1.25rem;
    }

    .muven-resource-missing-actions {
        flex-direction: column;

        .btn {
            justify-content: center;
            width: 100%;
        }
    }
}
</style>
